/**
 * Custom hook for tenant-scoped data operations
 * Automatically uses the current club context
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useClub } from '../contexts/ClubContext';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../services/firebase';
import {
  getTenantDocs,
  getTenantDoc,
  addTenantDoc,
  setTenantDoc,
  updateTenantDoc,
  deleteTenantDoc,
  getClubContent,
  setClubContent
} from '../services/tenantDb';

/**
 * Hook for fetching tenant-scoped collection data
 */
export function useTenantCollection(collectionName, additionalQueries = []) {
  const { clubId, loading: clubLoading } = useClub();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Memoize the queries string to use as a stable dependency
  const queriesKey = useMemo(() => JSON.stringify(additionalQueries), [additionalQueries]);

  const refresh = useCallback(async () => {
    if (!clubId) return;

    setLoading(true);
    try {
      const docs = await getTenantDocs(collectionName, clubId, additionalQueries);
      setData(docs);
      setError(null);
    } catch (err) {
      console.error(`Error fetching ${collectionName}:`, err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionName, clubId, queriesKey]);

  useEffect(() => {
    if (!clubLoading && clubId) {
      refresh();
    }
  }, [clubLoading, clubId, refresh]);

  return { data, loading: loading || clubLoading, error, refresh, setData };
}

/**
 * Hook for tenant-scoped CRUD operations
 */
export function useTenantCrud(collectionName) {
  const { clubId } = useClub();
  const [user] = useAuthState(auth);

  const add = useCallback(async (data) => {
    if (!clubId) throw new Error('No club context');
    return addTenantDoc(collectionName, data, clubId, user?.uid);
  }, [collectionName, clubId, user]);

  const update = useCallback(async (docId, data) => {
    if (!clubId) throw new Error('No club context');
    return updateTenantDoc(collectionName, docId, data, clubId, user?.uid);
  }, [collectionName, clubId, user]);

  const set = useCallback(async (docId, data, merge = true) => {
    if (!clubId) throw new Error('No club context');
    return setTenantDoc(collectionName, docId, data, clubId, user?.uid, merge);
  }, [collectionName, clubId, user]);

  const remove = useCallback(async (docId) => {
    if (!clubId) throw new Error('No club context');
    return deleteTenantDoc(collectionName, docId, clubId);
  }, [collectionName, clubId]);

  const get = useCallback(async (docId) => {
    if (!clubId) throw new Error('No club context');
    return getTenantDoc(collectionName, docId, clubId);
  }, [collectionName, clubId]);

  return { add, update, set, remove, get, clubId };
}

/**
 * Hook for club content (homepage, settings, etc.)
 */
export function useClubContent(contentType) {
  const { clubId, loading: clubLoading } = useClub();
  const [user] = useAuthState(auth);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!clubId) return;

    setLoading(true);
    try {
      const data = await getClubContent(contentType, clubId);
      setContent(data);
      setError(null);
    } catch (err) {
      console.error(`Error fetching ${contentType} content:`, err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [contentType, clubId]);

  useEffect(() => {
    if (!clubLoading && clubId) {
      refresh();
    }
  }, [clubLoading, clubId, refresh]);

  const save = useCallback(async (data) => {
    if (!clubId) throw new Error('No club context');
    const result = await setClubContent(contentType, data, clubId, user?.uid);
    setContent(result);
    return result;
  }, [contentType, clubId, user]);

  return { content, loading: loading || clubLoading, error, refresh, save };
}

/**
 * Hook for checking user's tenant access
 */
export function useTenantAccess() {
  const { clubId, hasAccess } = useClub();
  const [user] = useAuthState(auth);
  const [userClubId, setUserClubId] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        // Fetch user profile to get their clubId and role
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('../services/firebase');
        
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUserClubId(userData.clubId);
          setUserRole(userData.role);
        }
      } catch (err) {
        console.error('Error fetching user data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [user]);

  const canAccess = hasAccess(userClubId, userRole);
  const isSuperAdmin = userRole === 'super_admin';
  const isClubAdmin = userRole === 'club_admin' && canAccess;

  return {
    canAccess,
    isSuperAdmin,
    isClubAdmin,
    userClubId,
    userRole,
    currentClubId: clubId,
    loading
  };
}
